import apiClient from "../../api/api";

/**
 * @description Fetches the payment URL for a user based on their Sirius and personal emails.
 *
 * @async
 * @function requestGetPaymentUrl
 * @param {string} siriusEmail - The user's Sirius email address.
 * @param {string} personalEmail - The user's personal email address.
 *
 * @returns {Promise<any>} A promise that resolves with the payment URL data.
 *
 * @throws {Error} Throws an error if the API request fails.
 */
export const requestGetPaymentUrl = async(siriusEmail, personalEmail) => {
    const params = {
        userEmail: siriusEmail,
        userPersonalEmail: personalEmail
    }

    const getPaymentUrlResponse = await apiClient.get(import.meta.env.VITE_GET_PAYMENT_URL, {params})

    return getPaymentUrlResponse.data
}

/**
 * @description Fetches additional payments associated with a manager.
 *
 * @async
 * @function requestGetAdditionalPayments
 *
 * @returns {Promise<any>} A promise that resolves with the additional payments list.
 *
 * @throws {Error} Throws an error if the API request fails.
 */
export const requestGetAdditionalPayments = async(page = 1, pageSize = 25) => {
    const params= {
        pageSize,
        page
    }
    const getAdditionalPaymentsResponse = await apiClient.get(import.meta.env.VITE_PAYMENTS_BASE_URL, { params })

    return getAdditionalPaymentsResponse.data
}

/**
 * @description Creates a new additional payment.
 *
 * @async
 * @function requestCreateAdditionalPayment
 * @param {Object} additionalPayment - The additional payment payload to be created.
 *
 * @returns {Promise<any>} A promise that resolves with the creation response.
 *
 * @throws {Error} Throws an error if the API request fails.
 */
export const requestCreateAdditionalPayment = async(additionalPayment) => {
    const createAdditionalPaymentResponse = await apiClient.post(import.meta.env.VITE_PAYMENTS_BASE_URL, additionalPayment, {successToast:"Additional payment created"})

    return createAdditionalPaymentResponse
}

/**
 * @description Updates the status of an additional payment for a specific user.
 *
 * @async
 * @function requestUpdateAdditionalPaymentStatus
 * @param {number} requestId - The unique identifier of the request.
 * @param {Object} additionalPayment - The payload containing the updated payment status.
 *
 * @returns {Promise<any>} A promise that resolves with the update response.
 *
 * @throws {Error} Throws an error if the API request fails.
 */
export const requestUpdateAdditionalPaymentStatus = async(requestId, additionalPayment) => {
    const updateResponse = await apiClient.patch(import.meta.env.VITE_PAYMENTS_BASE_URL+`/${requestId}/status`, additionalPayment, {successToast:"Additional payment request updated"})

    return updateResponse
}

/**
 * @description Deletes an additional payment by its unique identifier.
 *
 * @async
 * @function requestDeleteAdditionalPayment
 * @param {number} additionalPaymentId - The unique identifier of the additional payment.
 *
 * @returns {Promise<any>} A promise that resolves with the delete response.
 *
 * @throws {Error} Throws an error if the API request fails.
 */
export const requestDeleteAdditionalPayment = async(additionalPaymentId) => {
    const deleteResponse = await apiClient.delete(import.meta.env.VITE_PAYMENTS_BASE_URL+`/${additionalPaymentId}`, {successToast:"Additional payment request deleted"})

    return deleteResponse
}